import React, { useState, useRef, useEffect } from 'react';
import { useAuth, TIER_LIMITS } from '../../contexts/AuthContext';
import { features } from '../../lib/features';
import { submitBugReport } from '../../lib/supabase';

const UserMenu = ({ onOpenSettings, onOpenSavedItineraries, onUpgrade, onOpenAnalytics }) => {
  const {
    user,
    profile,
    usage,
    tier,
    isAuthenticated,
    signOut,
    openLoginModal,
    setShowPersonalization,
    getTierLimits,
  } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [showBugModal, setShowBugModal] = useState(false);
  const [bugTitle, setBugTitle] = useState('');
  const [bugDescription, setBugDescription] = useState('');
  const [bugCategory, setBugCategory] = useState('general');
  const [bugSeverity, setBugSeverity] = useState('medium');
  const [bugSubmitting, setBugSubmitting] = useState(false);
  const [bugSubmitted, setBugSubmitted] = useState(false);
  const [bugError, setBugError] = useState(null);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBugSubmit = async (e) => {
    e.preventDefault();
    if (!bugTitle.trim() || !bugDescription.trim()) {
      setBugError('Please fill in both title and description');
      return;
    }
    setBugSubmitting(true);
    setBugError(null);
    try {
      const errorLogs = window.__wanderErrorLogs || [];
      await submitBugReport({
        userId: user?.id,
        userEmail: user?.email,
        title: bugTitle.trim(),
        description: bugDescription.trim(),
        severity: bugSeverity,
        category: bugCategory,
        appState: { timestamp: new Date().toISOString() },
        errorLogs: errorLogs.slice(-10),
      });
      setBugSubmitted(true);
      setTimeout(() => {
        setShowBugModal(false);
        setBugSubmitted(false);
        setBugTitle('');
        setBugDescription('');
        setBugCategory('general');
        setBugSeverity('medium');
      }, 2000);
    } catch (err) {
      console.error('Failed to submit bug report:', err);
      setBugError(err.message?.includes('timed out')
        ? 'Request timed out. Please check your connection and try again.'
        : 'Failed to submit. Please try again or email hello@arrivotravel.ai');
    } finally {
      setBugSubmitting(false);
    }
  };

  // Not authenticated - show sign in button
  if (!isAuthenticated) {
    return (
      <button
        onClick={() => openLoginModal()}
        className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm font-medium"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        Sign In
      </button>
    );
  }

  // Get tier badge color
  const tierColors = {
    explorer: 'bg-blue-100 text-blue-700',
    planner: 'bg-purple-100 text-purple-700',
    admin: 'bg-amber-100 text-amber-700',
    'super-admin': 'bg-rose-100 text-rose-700',
  };

  const tierLabels = {
    explorer: 'Explorer',
    planner: 'Planner',
    admin: 'Admin',
    'super-admin': 'Super Admin',
  };

  // Derive limits from tier config (source of truth), not from usage_tracking table
  const limits = TIER_LIMITS[tier] || TIER_LIMITS.explorer;
  const itineraryLimit = limits.itinerariesPerQuarter;
  const recLimit = limits.destinationRecommendations;

  // Calculate usage percentage
  const usagePercent = usage
    ? Math.round((usage.itineraries_generated / itineraryLimit) * 100)
    : 0;

  return (
    <div className="relative" ref={menuRef}>
      {/* Avatar button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
      >
        {profile?.avatar_url ? (
          <img
            src={profile.avatar_url}
            alt={profile.display_name || 'User'}
            className="w-8 h-8 rounded-full object-cover border-2 border-emerald-500"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-medium">
            {/* Multi-word initials: "Hope Game" → "HG", "Pedro" → "P".
                Previously only used the first character of display_name OR email,
                so users with multi-word names always saw a single letter (Hope's
                feedback flagged this as confusing). */}
            {(() => {
              const name = profile?.display_name || profile?.full_name || user?.user_metadata?.full_name;
              if (name && typeof name === 'string') {
                const parts = name.trim().split(/\s+/).filter(Boolean);
                if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                if (parts.length === 1 && parts[0].length > 0) return parts[0][0].toUpperCase();
              }
              // Whitespace-only email or null-ish falls through to '?'
              const fallback = (user?.email || '').trim() || '?';
              return fallback[0].toUpperCase();
            })()}
          </div>
        )}
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50" style={{ fontFamily: "'Raleway', sans-serif" }}>
          {/* User info header */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center gap-3">
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={profile.display_name || 'User'}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white font-medium text-lg">
                  {(profile?.display_name || user?.email || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {profile?.display_name || 'Traveler'}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tierColors[tier] || tierColors.explorer}`}>
                {tierLabels[tier] || 'Explorer'}
              </span>
            </div>
          </div>

          {/* Usage stats */}
          {usage && (
            <div className="px-4 py-3 border-b border-gray-100 space-y-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-600">Quarterly Usage</span>
              </div>

              {/* Admin gets unlimited - show different UI */}
              {tier === 'admin' ? (
                <div className="text-xs text-gray-500 py-2">
                  <span className="text-amber-600 font-medium">Unlimited access</span> - Admin tier
                </div>
              ) : (
                <>
                  {/* Itineraries */}
                  <div>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs text-gray-500">Itineraries</span>
                      <span className="text-xs text-gray-600">
                        {usage.itineraries_generated || 0}/{itineraryLimit}
                      </span>
                    </div>
                    <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          (usage.itineraries_generated || 0) >= (itineraryLimit) ? 'bg-red-500'
                          : (usage.itineraries_generated || 0) >= (itineraryLimit) * 0.8 ? 'bg-amber-500'
                          : 'bg-emerald-500'
                        }`}
                        style={{ width: `${Math.min(100, ((usage.itineraries_generated || 0) / (itineraryLimit)) * 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Destination Recommendations */}
                  <div>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs text-gray-500">Recommendations</span>
                      <span className="text-xs text-gray-600">
                        {usage.destination_recommendations || 0}/{recLimit}
                      </span>
                    </div>
                    <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          (usage.destination_recommendations || 0) >= (recLimit) ? 'bg-red-500'
                          : (usage.destination_recommendations || 0) >= (recLimit) * 0.8 ? 'bg-amber-500'
                          : 'bg-emerald-500'
                        }`}
                        style={{ width: `${Math.min(100, ((usage.destination_recommendations || 0) / (recLimit)) * 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Modifications info */}
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs text-gray-500">Mods/itinerary</span>
                    <span className="text-xs text-gray-600">
                      {(getTierLimits?.() || TIER_LIMITS.explorer).modificationsPerItinerary} allowed
                    </span>
                  </div>

                  {tier === 'explorer' && features.STRIPE_ENABLED && onUpgrade && (
                    <button
                      onClick={() => {
                        onUpgrade();
                        setIsOpen(false);
                      }}
                      className="w-full mt-2 px-3 py-1.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-xs font-medium rounded-lg hover:from-teal-600 hover:to-emerald-600 transition-all flex items-center justify-center gap-1.5"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      {usagePercent >= 80 ? 'Upgrade — Running Low!' : 'Upgrade to Planner'}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Menu items */}
          <div className="py-2">
            <button
              onClick={() => {
                setShowPersonalization(true);
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Edit Profile
            </button>

            <button
              onClick={() => {
                if (onOpenSavedItineraries) onOpenSavedItineraries();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              Saved Itineraries
            </button>

            <button
              onClick={() => {
                if (onOpenSettings) onOpenSettings();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </button>

            <button
              onClick={() => {
                setShowBugModal(true);
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Report Bug
            </button>
          </div>

          {/* Analytics Dashboard — super-admin only */}
          {tier === 'super-admin' && onOpenAnalytics && (
            <div className="border-t border-gray-100 py-2">
              <button
                onClick={() => {
                  onOpenAnalytics();
                  setIsOpen(false);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
              >
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Analytics Dashboard
              </button>
            </div>
          )}

          {/* Sign out */}
          <div className="border-t border-gray-100 py-2">
            <button
              onClick={async () => {
                await signOut();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign Out
            </button>
          </div>
        </div>
      )}
      {/* Bug Report Modal */}
      {showBugModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ fontFamily: "'Raleway', sans-serif" }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Report a Bug</h2>
              <button onClick={() => setShowBugModal(false)} className="text-white/70 hover:text-white">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-6">
              {bugSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-lg font-medium text-gray-900">Thank you!</p>
                  <p className="text-gray-500 mt-1">Your report has been submitted.</p>
                </div>
              ) : (
                <form onSubmit={handleBugSubmit}>
                  {bugError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm">{bugError}</p>
                    </div>
                  )}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">What went wrong?</label>
                    <input type="text" value={bugTitle} onChange={(e) => setBugTitle(e.target.value)} placeholder="Brief description of the issue" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent" maxLength={200} />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                    <textarea value={bugDescription} onChange={(e) => setBugDescription(e.target.value)} placeholder="What were you doing when this happened? What did you expect to happen?" rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none" maxLength={2000} />
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select value={bugCategory} onChange={(e) => setBugCategory(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                        <option value="general">General</option>
                        <option value="itinerary">Itinerary</option>
                        <option value="auth">Login/Account</option>
                        <option value="ui">Visual/UI</option>
                        <option value="performance">Slow/Stuck</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                      <select value={bugSeverity} onChange={(e) => setBugSeverity(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                        <option value="low">Low - Minor issue</option>
                        <option value="medium">Medium - Annoying</option>
                        <option value="high">High - Can't proceed</option>
                        <option value="critical">Critical - Broken</option>
                      </select>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">We'll automatically include some context to help us debug.</p>
                  <button type="submit" disabled={bugSubmitting} className="w-full bg-teal-500 hover:bg-teal-600 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    {bugSubmitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
